<template>
  <div class="signing-page" :class="{ 'signing-page--dark': isDark }">
    <div class="signing-overlay"/>
    <div class="signing-container">
      <!-- Header -->
      <div class="signing-header">
        <div class="signing-header__logo">
          <img v-if="orgLogo" :src="orgLogo" alt="Logo" class="signing-header__logo-img"/>
          <span v-else class="signing-header__logo-text">{{ orgName || 'CRM' }}</span>
        </div>
        <h1 class="signing-header__title">{{ msg('Contract Signing') }}</h1>
        <p class="signing-header__subtitle">{{ msg('Please review and sign all contracts below to continue') }}</p>
      </div>

      <!-- Progress -->
      <div class="signing-progress">
        <div class="signing-progress__bar">
          <div class="signing-progress__fill" :style="{ width: progressPercent + '%' }"/>
        </div>
        <span class="signing-progress__text">{{ signedCount }} / {{ totalCount }} {{ msg('contracts signed') }}</span>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-3">
        <b-spinner variant="primary" label="Loading..."/>
      </div>

      <!-- Empty -->
      <div v-else-if="contracts.length === 0" class="signing-empty">
        <feather-icon icon="CheckCircleIcon" size="48" class="text-success mb-1"/>
        <h4>{{ msg('All contracts are signed!') }}</h4>
        <p class="text-muted">{{ msg('You can now access the system.') }}</p>
        <b-button variant="primary" @click="enterSystem">{{ msg('Enter System') }}</b-button>
      </div>

      <!-- Contract Cards -->
      <div v-else class="signing-cards">
        <div v-for="contract in contracts" :key="contract.id" class="signing-card"
             :class="{ 'signing-card--signed': contract.status === 'SIGNED' }">
          <div class="signing-card__header">
            <div class="signing-card__type-badge" :class="'signing-card__type-badge--' + (contract.contractType || 'MANAGEMENT').toLowerCase()">
              {{ contractTypeLabel(contract.contractType) }}
            </div>
            <b-badge :variant="contract.status === 'SIGNED' ? 'success' : 'warning'" class="signing-card__status">
              {{ contract.status === 'SIGNED' ? msg('Signed') : msg('Pending') }}
            </b-badge>
          </div>

          <div class="signing-card__body">
            <h5 class="signing-card__number">{{ contract.contractNumber }}</h5>
            <div class="signing-card__meta">
              <div class="signing-card__meta-item">
                <feather-icon icon="HomeIcon" size="14"/>
                <span>{{ contract.clientCompanyName }}</span>
              </div>
              <div class="signing-card__meta-item">
                <feather-icon icon="GlobeIcon" size="14"/>
                <span>{{ contract.language === 'DE' ? 'Deutsch' : 'English' }}</span>
              </div>
              <div v-if="contract.createdAt" class="signing-card__meta-item">
                <feather-icon icon="CalendarIcon" size="14"/>
                <span>{{ formatDate(contract.createdAt) }}</span>
              </div>
            </div>
          </div>

          <!-- Provider signature (always signed) -->
          <div class="signing-card__provider-sig">
            <div class="signing-card__sig-label">{{ msg('Provider') }}</div>
            <div class="signing-card__sig-done">
              <feather-icon icon="CheckCircleIcon" size="14" class="text-success"/>
              <span class="signing-card__sig-ceo">Ofir Zukovitci</span>
              <span class="signing-card__sig-role">CEO</span>
            </div>
          </div>

          <div class="signing-card__actions">
            <b-button variant="outline-primary" size="sm" @click="previewContract(contract)">
              <feather-icon icon="EyeIcon" size="14" class="mr-25"/>{{ msg('Preview') }}
            </b-button>
            <b-button variant="outline-secondary" size="sm" @click="downloadContract(contract)">
              <feather-icon icon="DownloadIcon" size="14" class="mr-25"/>{{ msg('Download') }}
            </b-button>
            <b-button v-if="contract.status !== 'SIGNED'" variant="success" size="sm" @click="openSignDialog(contract)">
              <feather-icon icon="Edit3Icon" size="14" class="mr-25"/>{{ msg('Sign Contract') }}
            </b-button>
            <span v-else class="signing-card__signed-label">
              <feather-icon icon="CheckCircleIcon" size="16" class="text-success"/>
              {{ msg('Signed on') }} {{ formatDate(contract.signedAt) }}
            </span>
          </div>
        </div>
      </div>

      <!-- All signed banner -->
      <div v-if="!loading && contracts.length > 0 && allSigned" class="signing-done">
        <feather-icon icon="AwardIcon" size="32" class="signing-done__icon"/>
        <div>
          <h5 class="mb-25">{{ msg('All contracts signed!') }}</h5>
          <p class="mb-0 text-muted">{{ msg('You can now access the system.') }}</p>
        </div>
        <b-button variant="primary" size="sm" @click="enterSystem" class="ml-auto">
          {{ msg('Enter System') }}
          <feather-icon icon="ArrowRightIcon" size="14" class="ml-25"/>
        </b-button>
      </div>
    </div>

    <!-- Preview Modal -->
    <b-modal v-model="showPreview" size="lg" centered :title="previewTitle" hide-footer
             :modal-class="'signing-preview-modal' + (isDark ? ' signing-modal--dark' : '')">
      <div v-if="previewLoading" class="text-center py-3">
        <b-spinner variant="primary"/>
      </div>
      <iframe v-else class="signing-preview__iframe" :srcdoc="previewHtml" frameborder="0"/>
    </b-modal>

    <!-- Sign Modal -->
    <b-modal v-model="showSignConfirm" size="lg" centered :title="msg('Sign Contract')" hide-footer
             :modal-class="isDark ? 'signing-modal--dark' : ''" @shown="initCanvas">
      <div v-if="signTarget" class="signing-confirm">
        <!-- Contract info -->
        <div class="signing-confirm__info">
          <div class="signing-confirm__icon-wrap">
            <feather-icon icon="FileTextIcon" size="28"/>
          </div>
          <div>
            <h5 class="mb-0">{{ signTarget.contractNumber }}</h5>
            <small class="text-muted">{{ contractTypeLabel(signTarget.contractType) }}</small>
          </div>
        </div>

        <!-- Two signature columns -->
        <div class="signing-confirm__signatures">
          <!-- Provider side (already signed) -->
          <div class="signing-confirm__sig-col signing-confirm__sig-col--provider">
            <div class="signing-confirm__sig-title">{{ msg('Provider') }}</div>
            <div class="signing-confirm__sig-pad signing-confirm__sig-pad--done">
              <span class="signing-confirm__sig-script">Ofir Zukovitci</span>
              <feather-icon icon="CheckCircleIcon" size="18" class="signing-confirm__sig-check"/>
            </div>
            <div class="signing-confirm__sig-name-display">
              <strong>Ofir Zukovitci</strong> — CEO
            </div>
          </div>

          <!-- Client side (needs signature) -->
          <div class="signing-confirm__sig-col signing-confirm__sig-col--client">
            <div class="signing-confirm__sig-title">{{ msg('Client') }}</div>
            <div class="signing-confirm__sig-canvas-wrap">
              <canvas ref="signCanvas" class="signing-confirm__sig-canvas"
                      @mousedown="startDraw" @mousemove="draw" @mouseup="stopDraw" @mouseleave="stopDraw"
                      @touchstart.prevent="startDrawTouch" @touchmove.prevent="drawTouch" @touchend="stopDraw"/>
              <div v-if="!hasDrawn" class="signing-confirm__sig-placeholder">
                {{ msg('Draw your signature here') }}
              </div>
            </div>
            <div class="d-flex align-items-center justify-content-between mt-50">
              <b-button variant="flat-secondary" size="sm" @click="clearCanvas" :disabled="!hasDrawn">
                <feather-icon icon="TrashIcon" size="13" class="mr-25"/>{{ msg('Clear') }}
              </b-button>
            </div>
            <b-form-group :label="msg('Full Name (as signature)')" label-for="sign-name" class="mt-75 mb-50">
              <b-form-input id="sign-name" v-model="signName" :placeholder="msg('Enter your full name')" size="sm"/>
            </b-form-group>
          </div>
        </div>

        <!-- Agreement -->
        <div class="signing-confirm__agree">
          <b-form-checkbox v-model="signAgreed">
            {{ msg('I have read and agree to the terms of this contract') }}
          </b-form-checkbox>
        </div>

        <!-- Actions -->
        <div class="d-flex justify-content-end mt-1" style="gap:8px">
          <b-button variant="outline-secondary" size="sm" @click="showSignConfirm = false">{{ msg('Cancel') }}</b-button>
          <b-button variant="success" size="sm" :disabled="!canSign" @click="confirmSign">
            <b-spinner v-if="signing" small class="mr-50"/>
            <feather-icon v-else icon="CheckIcon" size="14" class="mr-25"/>
            {{ msg('Confirm & Sign') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'ContractSigning',
  data() {
    return {
      loading: true,
      contracts: [],
      showPreview: false,
      previewHtml: '',
      previewTitle: '',
      previewLoading: false,
      showSignConfirm: false,
      signTarget: null,
      signAgreed: false,
      signName: '',
      signing: false,
      isDrawing: false,
      hasDrawn: false,
      ctx: null,
      lastX: 0,
      lastY: 0,
    }
  },
  computed: {
    totalCount() { return this.contracts.length },
    signedCount() { return this.contracts.filter(c => c.status === 'SIGNED').length },
    allSigned() { return this.totalCount > 0 && this.signedCount === this.totalCount },
    progressPercent() { return this.totalCount > 0 ? (this.signedCount / this.totalCount) * 100 : 0 },
    orgLogo() {
      const org = this.$store.getters['data/getOrganization']
      return org ? org.logoLink : null
    },
    orgName() {
      const org = this.$store.getters['data/getOrganization']
      return org ? org.name : ''
    },
    isDark() {
      return document.body.classList.contains('dark-layout')
    },
    canSign() {
      return this.signAgreed && this.signName && this.hasDrawn && !this.signing
    },
  },
  mounted() {
    this.loadContracts()
  },
  methods: {
    async loadContracts() {
      this.loading = true
      try {
        const ans = await this.$store.dispatch('contract/getUnsignedContracts')
        const unsigned = ans.Contracts || []
        unsigned.forEach(c => {
          const existing = this.contracts.find(x => x.id === c.id)
          if (!existing) this.contracts.push(c)
        })
        if (this.contracts.length === 0) this.contracts = unsigned
      } catch (e) {
        if (this.contracts.length === 0) this.contracts = []
      } finally {
        this.loading = false
      }
    },
    contractTypeLabel(type) {
      const map = {
        MANAGEMENT: this.msg('Service Agreement'),
        LEGAL: this.msg('Legal Terms'),
        GENERAL: this.msg('General Agreement'),
      }
      return map[(type || '').toUpperCase()] || type
    },
    formatDate(d) {
      return d ? moment(d).format('DD.MM.YYYY HH:mm') : ''
    },
    previewContract(contract) {
      this.previewTitle = contract.contractNumber + ' — ' + this.contractTypeLabel(contract.contractType)
      this.previewHtml = contract.htmlBody || ''
      this.showPreview = true
    },
    downloadContract(contract) {
      this.$store.dispatch('contract/downloadPdf', { id: contract.id })
    },
    openSignDialog(contract) {
      this.signTarget = contract
      this.signAgreed = false
      this.signName = ''
      this.hasDrawn = false
      this.isDrawing = false
      this.ctx = null
      this.showSignConfirm = true
    },

    // Canvas signature pad
    initCanvas() {
      const canvas = this.$refs.signCanvas
      if (!canvas) return
      const wrapperRect = canvas.parentElement.getBoundingClientRect()

      // Draw using CSS pixels, but allocate using device pixels for crisp strokes.
      const cssWidth = Math.max(100, wrapperRect.width || 0)
      const cssHeight = Math.max(120, wrapperRect.height || 120)
      const dpr = window.devicePixelRatio || 1

      canvas.style.width = cssWidth + 'px'
      canvas.style.height = cssHeight + 'px'
      canvas.width = Math.floor(cssWidth * dpr)
      canvas.height = Math.floor(cssHeight * dpr)

      this.ctx = canvas.getContext('2d')
      // Map canvas coordinates to CSS pixels.
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      this.ctx.fillStyle = '#ffffff'
      this.ctx.fillRect(0, 0, cssWidth, cssHeight)
      this.ctx.strokeStyle = '#1e293b'
      this.ctx.fillStyle = '#1e293b'
      this.ctx.lineWidth = 2.5
      this.ctx.lineCap = 'round'
      this.ctx.lineJoin = 'round'
      this.hasDrawn = false
    },
    getCanvasPos(e) {
      const canvas = this.$refs.signCanvas
      const rect = canvas.getBoundingClientRect()
      return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    },
    startDraw(e) {
      this.isDrawing = true
      const pos = this.getCanvasPos(e)
      this.lastX = pos.x
      this.lastY = pos.y
      // Draw a dot immediately so the user sees feedback even on small moves.
      if (this.ctx) {
        this.ctx.strokeStyle = '#1e293b'
        this.ctx.fillStyle = '#1e293b'
        this.ctx.beginPath()
        this.ctx.arc(this.lastX, this.lastY, 1.7, 0, Math.PI * 2)
        this.ctx.fill()
      }
      this.hasDrawn = true
    },
    draw(e) {
      if (!this.isDrawing || !this.ctx) return
      const pos = this.getCanvasPos(e)
      this.ctx.strokeStyle = '#1e293b'
      this.ctx.beginPath()
      this.ctx.moveTo(this.lastX, this.lastY)
      this.ctx.lineTo(pos.x, pos.y)
      this.ctx.stroke()
      this.lastX = pos.x
      this.lastY = pos.y
      this.hasDrawn = true
    },
    stopDraw() {
      this.isDrawing = false
    },
    startDrawTouch(e) {
      const touch = e.touches[0]
      const canvas = this.$refs.signCanvas
      const rect = canvas.getBoundingClientRect()
      this.isDrawing = true
      this.lastX = touch.clientX - rect.left
      this.lastY = touch.clientY - rect.top
      if (this.ctx) {
        this.ctx.strokeStyle = '#1e293b'
        this.ctx.fillStyle = '#1e293b'
        this.ctx.beginPath()
        this.ctx.arc(this.lastX, this.lastY, 1.7, 0, Math.PI * 2)
        this.ctx.fill()
      }
      this.hasDrawn = true
    },
    drawTouch(e) {
      if (!this.isDrawing || !this.ctx) return
      const touch = e.touches[0]
      const canvas = this.$refs.signCanvas
      const rect = canvas.getBoundingClientRect()
      const x = touch.clientX - rect.left
      const y = touch.clientY - rect.top
      this.ctx.strokeStyle = '#1e293b'
      this.ctx.beginPath()
      this.ctx.moveTo(this.lastX, this.lastY)
      this.ctx.lineTo(x, y)
      this.ctx.stroke()
      this.lastX = x
      this.lastY = y
      this.hasDrawn = true
    },
    clearCanvas() {
      if (!this.ctx) return
      const canvas = this.$refs.signCanvas
      const rect = canvas.getBoundingClientRect()
      const cssWidth = rect.width || 0
      const cssHeight = rect.height || 120
      this.ctx.fillStyle = '#ffffff'
      this.ctx.fillRect(0, 0, cssWidth, cssHeight)
      this.ctx.strokeStyle = '#1e293b'
      this.ctx.fillStyle = '#1e293b'
      this.hasDrawn = false
    },

    getSignatureDataUrl() {
      const canvas = this.$refs.signCanvas
      if (!canvas) return null
      return canvas.toDataURL('image/png')
    },
    async confirmSign() {
      if (!this.canSign) return
      this.signing = true
      try {
        const signatureData = this.getSignatureDataUrl()
        const ans = await this.$store.dispatch('contract/signContract', {
          id: this.signTarget.id,
          signatureData,
          signerName: this.signName,
        })
        const signed = ans.Contract || ans
        const idx = this.contracts.findIndex(c => c.id === this.signTarget.id)
        if (idx >= 0) {
          this.$set(this.contracts, idx, { ...this.contracts[idx], status: 'SIGNED', signedAt: signed.signedAt || new Date() })
        }
        this.showSignConfirm = false
      } finally {
        this.signing = false
      }
    },
    enterSystem() {
      this.$router.push('/')
    },
  }
}
</script>

<style lang="scss" scoped>
/* ─── Page ─── */
.signing-page {
  min-height: 100vh; position: relative; display: flex; align-items: flex-start; justify-content: center;
  padding: 40px 20px;
}
.signing-overlay {
  position: fixed; inset: 0; z-index: 0;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 30%, #f5f3ff 70%, #fce7f3 100%);
}
.signing-container {
  position: relative; z-index: 1; max-width: 780px; width: 100%;
}

/* ─── Header ─── */
.signing-header {
  text-align: center; margin-bottom: 28px;
  &__logo { margin-bottom: 16px; }
  &__logo-img { max-height: 48px; }
  &__logo-text { font-size: 1.6rem; font-weight: 800; color: #4f46e5; }
  &__title { font-size: 1.6rem; font-weight: 700; color: #1e293b; margin-bottom: 6px; }
  &__subtitle { font-size: .95rem; color: #64748b; margin-bottom: 0; }
}

/* ─── Progress ─── */
.signing-progress {
  background: #fff; border-radius: 12px; padding: 16px 20px; margin-bottom: 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
  display: flex; align-items: center; gap: 14px;
  &__bar { flex: 1; height: 8px; background: #e5e7eb; border-radius: 4px; overflow: hidden; }
  &__fill { height: 100%; background: linear-gradient(90deg, #4f46e5, #7c3aed); border-radius: 4px; transition: width .5s ease; }
  &__text { font-size: .85rem; font-weight: 600; color: #6366f1; white-space: nowrap; }
}

/* ─── Cards ─── */
.signing-cards { display: flex; flex-direction: column; gap: 14px; }
.signing-card {
  background: #fff; border-radius: 12px; padding: 20px 24px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06); border-left: 4px solid #e5e7eb; transition: all .2s;
  &:hover { box-shadow: 0 4px 12px rgba(0,0,0,.08); }
  &--signed { border-left-color: #10b981; }
  &__header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
  &__type-badge {
    font-size: .75rem; font-weight: 700; text-transform: uppercase; letter-spacing: .5px;
    padding: 3px 10px; border-radius: 6px;
    &--management { background: #ede9fe; color: #7c3aed; }
    &--legal { background: #dbeafe; color: #1e40af; }
    &--general { background: #d1fae5; color: #059669; }
  }
  &__body { margin-bottom: 10px; }
  &__number { font-size: 1rem; font-weight: 700; color: #1e293b; margin-bottom: 8px; }
  &__meta { display: flex; flex-wrap: wrap; gap: 16px; }
  &__meta-item { display: flex; align-items: center; gap: 5px; font-size: .85rem; color: #64748b; }

  &__provider-sig {
    display: flex; align-items: center; gap: 10px; padding: 8px 12px;
    background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; margin-bottom: 12px;
  }
  &__sig-label { font-size: .75rem; font-weight: 600; text-transform: uppercase; color: #16a34a; letter-spacing: .5px; }
  &__sig-done { display: flex; align-items: center; gap: 6px; }
  &__sig-ceo { font-family: 'Georgia', serif; font-size: .95rem; font-style: italic; color: #15803d; }
  &__sig-role { font-size: .75rem; color: #4ade80; }

  &__actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  &__signed-label { display: flex; align-items: center; gap: 5px; font-size: .85rem; color: #10b981; font-weight: 500; }
}

/* ─── Done banner ─── */
.signing-done {
  background: #fff; border-radius: 12px; padding: 20px 24px; margin-top: 16px;
  display: flex; align-items: center; gap: 16px;
  border: 2px solid #10b981; box-shadow: 0 2px 8px rgba(16,185,129,.15);
  &__icon { color: #10b981; }
}

/* ─── Empty state ─── */
.signing-empty {
  text-align: center; padding: 60px 20px;
  background: #fff; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,.06);
  h4 { color: #1e293b; }
}

/* ─── Sign Confirm Modal ─── */
.signing-confirm {
  &__info {
    display: flex; align-items: center; gap: 14px; padding-bottom: 16px;
    margin-bottom: 16px; border-bottom: 1px solid #e5e7eb;
  }
  &__icon-wrap { color: #6366f1; flex-shrink: 0; }
  &__signatures { display: flex; gap: 20px; margin-bottom: 16px; }
  &__sig-col {
    flex: 1; min-width: 0;
    &--provider {}
    &--client {}
  }
  &__sig-title {
    font-size: .75rem; font-weight: 700; text-transform: uppercase; letter-spacing: .5px;
    color: #64748b; margin-bottom: 8px;
  }
  &__sig-pad {
    border: 2px dashed #d1d5db; border-radius: 10px; padding: 16px;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    min-height: 120px; position: relative;
    &--done {
      border-color: #86efac; background: #f0fdf4;
      flex-direction: column;
    }
  }
  &__sig-script {
    font-family: 'Georgia', 'Times New Roman', serif; font-size: 1.4rem;
    font-style: italic; color: #15803d;
  }
  &__sig-check { color: #16a34a; }
  &__sig-name-display {
    font-size: .8rem; color: #64748b; margin-top: 6px; text-align: center;
  }
  &__sig-canvas-wrap {
    border: 2px dashed #d1d5db; border-radius: 10px; position: relative;
    overflow: hidden; min-height: 120px; cursor: crosshair; background: #fff;
  }
  &__sig-canvas { display: block; width: 100%; }
  &__sig-placeholder {
    position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
    font-size: .85rem; color: #9ca3af; pointer-events: none;
  }
  &__agree { margin-top: 8px; }
}

/* ─── Preview ─── */
.signing-preview__iframe {
  width: 100%; min-height: 70vh; border: none; border-radius: 8px; background: #fff;
}

/* ───────────────────────── DARK MODE ───────────────────────── */
.signing-page--dark {
  .signing-overlay {
    background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 40%, #1a1a2e 70%, #0f172a 100%);
  }
  .signing-header {
    &__title { color: #e2e8f0; }
    &__subtitle { color: #94a3b8; }
    &__logo-text { color: #818cf8; }
  }
  .signing-progress {
    background: #1e293b; box-shadow: 0 1px 4px rgba(0,0,0,.3);
    .signing-progress__bar { background: #334155; }
    .signing-progress__text { color: #a5b4fc; }
  }
  .signing-card {
    background: #1e293b; border-left-color: #334155;
    box-shadow: 0 1px 4px rgba(0,0,0,.25);
    &:hover { box-shadow: 0 4px 12px rgba(0,0,0,.35); }
    &--signed { border-left-color: #059669; }
    .signing-card__number { color: #e2e8f0; }
    .signing-card__meta-item { color: #94a3b8; }
    .signing-card__type-badge {
      &--management { background: rgba(124,58,237,.2); color: #a78bfa; }
      &--legal { background: rgba(30,64,175,.2); color: #93c5fd; }
      &--general { background: rgba(5,150,105,.2); color: #6ee7b7; }
    }
    .signing-card__provider-sig {
      background: rgba(16,185,129,.1); border-color: rgba(16,185,129,.25);
    }
    .signing-card__sig-label { color: #34d399; }
    .signing-card__sig-ceo { color: #34d399; }
    .signing-card__sig-role { color: #6ee7b7; }
  }
  .signing-done {
    background: #1e293b; border-color: #059669; box-shadow: 0 2px 8px rgba(5,150,105,.2);
    .signing-done__icon { color: #34d399; }
    h5 { color: #e2e8f0; }
  }
  .signing-empty {
    background: #1e293b; box-shadow: 0 1px 4px rgba(0,0,0,.3);
    h4 { color: #e2e8f0; }
  }
}

@media (max-width: 640px) {
  .signing-confirm__signatures { flex-direction: column; }
}
</style>

<style lang="scss">
/* Global (unscoped) dark overrides for modals */
.signing-modal--dark .modal-content {
  background: #1e293b; border-color: #334155; color: #e2e8f0;
}
.signing-modal--dark .modal-header {
  background: #1e293b; border-bottom-color: #334155;
  .modal-title { color: #e2e8f0; }
  .close { color: #94a3b8; text-shadow: none; }
}
.signing-modal--dark .modal-body {
  background: #1e293b; color: #e2e8f0;
}
.signing-modal--dark .signing-confirm__info {
  border-bottom-color: #334155;
  h5 { color: #e2e8f0; }
  .text-muted { color: #94a3b8 !important; }
}
.signing-modal--dark .signing-confirm__sig-title { color: #94a3b8; }
.signing-modal--dark .signing-confirm__sig-pad--done {
  border-color: rgba(16,185,129,.3); background: rgba(16,185,129,.08);
}
.signing-modal--dark .signing-confirm__sig-canvas-wrap {
  border-color: #475569; background: #fff;
}
.signing-modal--dark .signing-confirm__sig-placeholder { color: #64748b; }
.signing-modal--dark .signing-confirm__sig-name-display { color: #94a3b8; }
.signing-modal--dark .signing-confirm__icon-wrap { color: #818cf8; }
.signing-modal--dark label { color: #cbd5e1; }
.signing-modal--dark .form-control {
  background: #0f172a; border-color: #475569; color: #e2e8f0;
  &::placeholder { color: #64748b; }
}
.signing-modal--dark .custom-checkbox .custom-control-label { color: #cbd5e1; }
</style>
